import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import { RuleDialogProps } from '@app/src/types/questionRules'
import { Question } from '@app/src/types/resourceExplorer'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { Operators } from '../ResourceCollection/Filters/useFilters'

const RulesDialog = ({ open, closeDialog, rulesForCurrentQuestion, questionsInTemplate }: RuleDialogProps) => {
  const { formatMessage } = useIntl()

  const { items: questions, isLoading: isLoadingQuestions } = useFetchCollectionWithPost<Question>({
    key: FetchKey.Questions,
    endpoint: endpoints.questionCollection,
    payload: {
      filter: [
        {
          name: 'id',
          filters: [
            { value: questionsInTemplate.map(question => question.questionId ?? question.id), operator: Operators.In },
          ],
        },
      ],
      include: [],
    },
    options: { enabled: Boolean(questionsInTemplate.length) },
  })

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
      <DialogTitle>{formatMessage({ id: 'templateBuilder.questionRules.rulesAffectingThisQuestion' })}</DialogTitle>
      <DialogContent>
        {isLoadingQuestions ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {rulesForCurrentQuestion?.map(rule => {
              const sourceQuestion = questions.find((q: Question) => Number(q.id) === Number(rule.questionId))
              return (
                <Box key={rule.id} p={2} border="1px solid" borderColor="grey.300" borderRadius={1}>
                  <Typography variant="body1">
                    {rule.conditionValue === QuestionRulesConditions.Skipped
                      ? formatMessage(
                          { id: 'templateBuilder.questionRules.skippedBySkipped' },
                          {
                            sourceQuestion: `${sourceQuestion?.questionText}`,
                            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                          },
                        )
                      : formatMessage(
                          { id: 'templateBuilder.questionRules.skipCondition' },
                          {
                            sourceQuestion: `${sourceQuestion?.questionText}`,
                            condition:
                              rule.conditionValue === QuestionRulesConditions.FileUploaded
                                ? formatMessage({ id: `templateBuilder.questionRules.${rule.conditionValue}` })
                                : rule.conditionValue,
                            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                          },
                        )}
                  </Typography>
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{formatMessage({ id: 'general.close' })}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default RulesDialog
